import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { parseModels, parseVairants } from '../../utils/parseCiems';

export const getShopifyCIEMList = createAsyncThunk('shopify/getShopifyCIEMList', async () => {
    const getShopifyCiems = await fetch(`${process.env.REACT_APP_API_SHOPIFY_BASE}`, {
        method: 'POST',
        headers: {
            'X-Shopify-Storefront-Access-Token': `${process.env.REACT_APP_API_SHOPIFY_KEY}`,
            'Content-type': 'application/json'
        },
        body: JSON.stringify({
            query: `query FetchCollectionProducts($title: String) {
                collections(query: $title, first: 1) {
                    edges {
                      node {
                        id
                        products(first: 20) {
                          edges {
                            node {
                              id
                              title
                              productType
                              variants(first: 1) {
                                  nodes {
                                    id
                                    title
                                    sku
                                    price
                                    {
                                        amount
                                        currencyCode
                                    }
                                  }
                              }
                        }
                      }
                    }
                  }
                }
            }
            }
            `,
            variables: {
                title: 'Custom IEM'
            }
        })
    });
    const request = await getShopifyCiems.json();
    const data = parseModels(request.data);
    // const response = await fetch(`${process.env.REACT_APP_API_BASE}/ciems`);
    // return response.json();
    return data;
});

export const createVariantForCIEM = createAsyncThunk(
    'shopify/createVariantForCIEM',
    async ({ id, price, sku }, { rejectWithValue }) => {
        const request = await fetch(`${process.env.REACT_APP_API_BASE}/generate-variant`, {
            method: 'POST',
            headers: {
                'Content-type': 'application/json'
            },
            body: JSON.stringify({
                title: `${price}`,
                id: id,
                price: price,
                sku: sku,
                company: process.env.REACT_APP_COMPANY_NAME
            })
        });
        const response = await request.json();
        // Shopify REST cannot create variants on products with >100 variants.
        // Surface API errors so add-to-cart can fall back to an existing match.
        if (!request.ok) {
            return rejectWithValue(response);
        }
        return response;
    }
);

export const getVariantsForCIEM = createAsyncThunk('shopify/getVariantsForCIEM', async (id) => {
    const fetchVariantPage = async (after = null) => {
        const getVariants = await fetch(`${process.env.REACT_APP_API_SHOPIFY_BASE}`, {
            method: 'POST',
            headers: {
                'X-Shopify-Storefront-Access-Token': `${process.env.REACT_APP_API_SHOPIFY_KEY}`,
                'Content-type': 'application/json'
            },
            body: JSON.stringify({
                query: `query getProductById($id: ID!, $after: String) {
        product(id: $id) {
          title
          description
          variants(first: 250, after: $after) {
            nodes {
              id
              title
              sku
              price {
                amount
                currencyCode
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }`,
                variables: {
                    id: id,
                    after: after
                }
            })
        });
        return getVariants.json();
    };

    // Shopify caps each page at 250; paginate so products with >100 variants
    // (e.g. A18s with 102) are fully loaded before add-to-cart matching.
    let allVariantNodes = [];
    let after = null;
    let hasNextPage = true;
    let productTitle = '';
    let productDescription = '';

    while (hasNextPage) {
        const request = await fetchVariantPage(after);
        const product = request?.data?.product;
        if (!product) {
            throw new Error('Failed to fetch product variants');
        }
        productTitle = product.title;
        productDescription = product.description;
        allVariantNodes = allVariantNodes.concat(product.variants.nodes);
        hasNextPage = product.variants.pageInfo.hasNextPage;
        after = product.variants.pageInfo.endCursor;
    }

    const data = parseVairants(
        {
            product: {
                title: productTitle,
                description: productDescription,
                variants: { nodes: allVariantNodes }
            }
        },
        id
    );
    return data;
    // const response = await fetch(`${process.env.REACT_APP_API_BASE}/variants?id=${id}`);
    // return response.json();
});

export const submitOrder = createAsyncThunk('shopify/submitOrder', async (orderDetails) => {
    // addToCartHandler(orderDetails);

    const resp = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: orderDetails
    })
        .then((response) => {
            return response.json();
        })
        .catch((error) => {
            // Product not found
            // Need to generate new variant ID and try again
            console.error('Error:', error);
        });

    if (resp.status === 200) {
        return true;
    }
    console.log(resp.status);
    return false;
});

const initialState = {
    ciems: [],
    currentCiem: { id: 0, title: '', currency: '', tags: '', sku: '', variants: [] },
    newVariant: { id: 0, title: '', price: 0, sku: '' },
    submitted: false
};

const shopifySlice = createSlice({
    name: 'shopify',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // eslint-disable-next-line
        builder.addCase(getShopifyCIEMList.pending, (state, action) => {
            state.ciems = [];
        });
        builder.addCase(getShopifyCIEMList.fulfilled, (state, action) => {
            state.ciems = action.payload;
        });
        builder.addCase(getShopifyCIEMList.rejected, (state, action) => {
            console.log(action.payload);
        });
        // eslint-disable-next-line no-unused-vars
        builder.addCase(getVariantsForCIEM.fulfilled, (state, action) => {
            state.currentCiem.variants = action.payload.variants;
            state.currentCiem.title = action.payload.title;
            state.currentCiem.currency = action.payload.currency;
            state.currentCiem.id = action.payload.id;
            state.currentCiem.sku = action.payload.sku;
            // state.currentCiem.tags = action.payload.tags;
        });
        builder.addCase(createVariantForCIEM.pending, (state) => {
            state.newVariant = {
                id: 0,
                title: '',
                price: 0,
                sku: ''
            };
        });
        builder.addCase(createVariantForCIEM.fulfilled, (state, action) => {
            console.log(action.payload);
            state.newVariant.id = action.payload.id;
            state.newVariant.title = action.payload.title;
            state.newVariant.price = action.payload.price;
            state.newVariant.sku = action.payload.sku;
        });
        builder.addCase(submitOrder.pending, (state) => {
            state.submitted = false;
        });
        builder.addCase(submitOrder.fulfilled, (state) => {
            state.submitted = true;
        });
    }
});
const shopifyReducer = shopifySlice.reducer;
export default shopifyReducer;
